import { useState } from 'react'
import { useAuth } from '../components/AppContext'
import { useNavigate } from 'react-router-dom'
import { Zap, ChevronRight, ArrowRight, Check, Upload } from 'lucide-react'

const WHO_OPTIONS = [
  { value:'freelancer',    emoji:'💼', label:'Freelancer / Consultant',  sub:'Client projects, invoices' },
  { value:'uber',          emoji:'🚗', label:'Uber / Taxi / Delivery',   sub:'Ride-hailing or delivery driver' },
  { value:'tradesperson',  emoji:'🔧', label:'Tradesperson / Contractor', sub:'Builder, plumber, electrician' },
  { value:'landlord',      emoji:'🏠', label:'Landlord',                  sub:'Rental income' },
  { value:'ecommerce',     emoji:'🛍️', label:'eBay / Amazon / Etsy',      sub:'Online selling' },
  { value:'other',         emoji:'✨', label:'Other self-employed',        sub:'Any other sole trader' },
]

const WHO_INSIGHT = {
  freelancer:   { headline: "You're all set, {name}.", body: "Upload your bank statement and the AI will categorise your invoices, software costs and client expenses automatically.", saving: "Most freelancers find their first quarter takes under 20 minutes." },
  uber:         { headline: "You're in the right place, {name}.", body: "Mileage is your biggest tax saving — 45p per mile. Upload your statement and Quarterly tracks everything automatically.", saving: "Most drivers save £2,000–£4,500 per year on mileage alone." },
  tradesperson: { headline: "Ready to go, {name}.", body: "Tools, materials, van costs, fuel — every allowable expense categorised automatically once you upload your statement.", saving: "Most tradespeople find more deductions than they expected." },
  landlord:     { headline: "You're set up, {name}.", body: "Upload your statement and Quarterly separates rental income from personal, tracks mortgage interest, repairs and management fees.", saving: "Rental income tracking takes minutes, not hours." },
  ecommerce:    { headline: "Let's get started, {name}.", body: "Platform fees, stock costs, postage, packaging — the AI handles the categories. Just upload your bank statement.", saving: "Most sellers get through a full quarter in under 20 minutes." },
  other:        { headline: "You're all set, {name}.", body: "Upload your bank statement and Quarterly AI will categorise your transactions against HMRC categories automatically.", saving: "Most sole traders are done in under 20 minutes per quarter." },
}

export default function Onboarding() {
  const { saveProfile, user } = useAuth()
  const navigate = useNavigate()
  const [step, setStep]       = useState(0)
  const [who, setWho]         = useState('')
  const [vatReg, setVatReg]   = useState(null)
  const [form, setForm]       = useState({ full_name:'', onboarded:true })
  const [loading, setLoading] = useState(false)

  const firstName = form.full_name?.split(' ')[0] || 'there'
  const insight   = WHO_INSIGHT[who] || WHO_INSIGHT.other

  async function finish() {
    setLoading(true)
    await saveProfile({
      ...form,
      user_type:      who,
      vat_registered: vatReg === 'yes',
      email:          user.email,
      onboarded:      true,
    })
    setLoading(false)
    setStep(3) // go to momentum screen, not dashboard
  }

  // ── Step 0: Who are you? ──
  if (step === 0) return (
    <div style={WRAP}>
      <div style={{ width:'100%', maxWidth:480 }} className="fade-up">
        <div style={{ textAlign:'center', marginBottom:32 }}>
          <div style={{ width:52, height:52, borderRadius:14, background:'var(--teal)', display:'inline-flex', alignItems:'center', justifyContent:'center', marginBottom:14 }}>
            <Zap size={22} color="var(--navy)" strokeWidth={2.5} />
          </div>
          <h1 style={{ fontFamily:'var(--font-display)', fontSize:26, marginBottom:8 }}>Welcome to Quarterly 👋</h1>
          <p style={{ color:'var(--slate)', fontSize:14, lineHeight:1.6 }}>
            One question to get started — what best describes you?
          </p>
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {WHO_OPTIONS.map(opt => (
            <button key={opt.value}
              onClick={() => { setWho(opt.value); setStep(1) }}
              style={{ display:'flex', alignItems:'center', gap:14, padding:'14px 18px', background:'var(--navy-card)', border:'1px solid var(--navy-border)', borderRadius:'var(--r)', cursor:'pointer', transition:'all .15s', textAlign:'left', width:'100%' }}
              onMouseEnter={e => e.currentTarget.style.borderColor='rgba(0,212,170,.4)'}
              onMouseLeave={e => e.currentTarget.style.borderColor='var(--navy-border)'}
            >
              <span style={{ fontSize:24, flexShrink:0 }}>{opt.emoji}</span>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:14, fontWeight:600, color:'var(--white)' }}>{opt.label}</div>
                <div style={{ fontSize:12, color:'var(--slate)', marginTop:1 }}>{opt.sub}</div>
              </div>
              <ChevronRight size={16} color="var(--slate-dim)" />
            </button>
          ))}
        </div>
        <p style={{ textAlign:'center', fontSize:11, color:'var(--slate-dim)', marginTop:20 }}>
          This helps us show the right allowances and reminders for your situation
        </p>
      </div>
    </div>
  )

  // ── Step 1: Your name ──
  if (step === 1) return (
    <div style={WRAP}>
      <div style={{ width:'100%', maxWidth:420 }} className="fade-up">
        <div style={{ textAlign:'center', marginBottom:32 }}>
          <div style={{ fontSize:40, marginBottom:12 }}>{WHO_OPTIONS.find(o => o.value === who)?.emoji}</div>
          <h1 style={{ fontFamily:'var(--font-display)', fontSize:26, marginBottom:8 }}>
            {who === 'uber' ? "You're in the right place." : "Almost there."}
          </h1>
          {who === 'uber' ? (
            <p style={{ color:'var(--slate)', fontSize:14, lineHeight:1.65 }}>
              Quarterly tracks your mileage, fuel and quarterly filings — built for drivers who'd rather be on the road than doing admin.
            </p>
          ) : (
            <p style={{ color:'var(--slate)', fontSize:14, lineHeight:1.65 }}>
              Just your name and we'll set everything up.
            </p>
          )}
        </div>
        <div className="card">
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div className="field">
              <label>Your first name</label>
              <input value={form.full_name} onChange={e => setForm(p=>({...p, full_name:e.target.value}))} placeholder="e.g. James" autoFocus style={{ fontSize:16 }} />
            </div>
            {who === 'uber' && (
              <div style={{ background:'var(--teal-glow)', border:'1px solid rgba(0,212,170,.2)', borderRadius:9, padding:'12px 14px', fontSize:12, color:'var(--slate)', lineHeight:1.65 }}>
                <div style={{ fontWeight:600, color:'var(--teal)', marginBottom:4 }}>🚗 Your biggest tax saving: mileage</div>
                HMRC lets you claim 45p per mile for the first 10,000 business miles. Most drivers save £2,000–£4,500/year just from this.
              </div>
            )}
            {who === 'tradesperson' && (
              <div style={{ background:'var(--teal-glow)', border:'1px solid rgba(0,212,170,.2)', borderRadius:9, padding:'12px 14px', fontSize:12, color:'var(--slate)', lineHeight:1.65 }}>
                <div style={{ fontWeight:600, color:'var(--teal)', marginBottom:4 }}>🔧 Tools, materials and van costs are all deductible</div>
                Quarterly tracks all your business expenses automatically.
              </div>
            )}
            <button className="btn btn-primary btn-full btn-lg" onClick={() => setStep(2)} disabled={!form.full_name.trim()} style={{ marginTop:4 }}>
              Continue <ArrowRight size={16} />
            </button>
          </div>
        </div>
        <button onClick={() => setStep(0)} style={{ display:'block', margin:'16px auto 0', background:'none', border:'none', color:'var(--slate-dim)', fontSize:12, cursor:'pointer', textDecoration:'underline' }}>
          ← Go back
        </button>
      </div>
    </div>
  )

  // ── Step 2: VAT registered? ──
  if (step === 2) return (
    <div style={WRAP}>
      <div style={{ width:'100%', maxWidth:420 }} className="fade-up">
        <div style={{ textAlign:'center', marginBottom:32 }}>
          <div style={{ fontSize:36, marginBottom:12 }}>🧾</div>
          <h1 style={{ fontFamily:'var(--font-display)', fontSize:26, marginBottom:8 }}>Are you VAT registered?</h1>
          <p style={{ color:'var(--slate)', fontSize:14, lineHeight:1.65 }}>
            This affects how Quarterly handles your receipts and expenses. You can update this in Settings any time.
          </p>
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:12, marginBottom:24 }}>
          {[
            { val:'yes', title:"Yes, I'm VAT registered",     sub:"I have a VAT number and charge VAT to clients",              bg:'var(--teal-glow)',           border:'rgba(0,212,170,.5)' },
            { val:'no',  title:"No, I'm not VAT registered",  sub:"My turnover is under £90,000 or I don't need to register",   bg:'rgba(255,255,255,.04)',      border:'rgba(255,255,255,.25)' },
          ].map(opt => (
            <button key={opt.val} onClick={() => setVatReg(opt.val)}
              style={{ display:'flex', alignItems:'center', gap:14, padding:'16px 18px', background: vatReg === opt.val ? opt.bg : 'var(--navy-card)', border:`1px solid ${vatReg === opt.val ? opt.border : 'var(--navy-border)'}`, borderRadius:'var(--r)', cursor:'pointer', transition:'all .15s', textAlign:'left', width:'100%' }}>
              <div style={{ width:36, height:36, borderRadius:10, background:'rgba(255,255,255,.05)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                {vatReg === opt.val ? <Check size={18} color={opt.val === 'yes' ? 'var(--teal)' : 'var(--white)'} /> : <span style={{ fontSize:18 }}>{opt.val === 'yes' ? '✅' : '❌'}</span>}
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:14, fontWeight:600, color:'var(--white)' }}>{opt.title}</div>
                <div style={{ fontSize:12, color:'var(--slate)', marginTop:2 }}>{opt.sub}</div>
              </div>
            </button>
          ))}
        </div>
        <button className="btn btn-primary btn-full btn-lg" onClick={finish} disabled={loading || vatReg === null}>
          {loading ? <span className="spin" /> : <>Continue <ArrowRight size={16} /></>}
        </button>
        <button onClick={() => setStep(1)} style={{ display:'block', margin:'16px auto 0', background:'none', border:'none', color:'var(--slate-dim)', fontSize:12, cursor:'pointer', textDecoration:'underline' }}>
          ← Go back
        </button>
      </div>
    </div>
  )

  // ── Step 3: Momentum — add your statement NOW ──
  if (step === 3) return (
    <div style={WRAP}>
      <div style={{ width:'100%', maxWidth:440 }} className="fade-up">
        <div style={{ textAlign:'center', marginBottom:28 }}>
          <div style={{ fontSize:48, marginBottom:12 }}>{WHO_OPTIONS.find(o => o.value === who)?.emoji}</div>
          <h1 style={{ fontFamily:'var(--font-display)', fontSize:28, marginBottom:10, color:'var(--white)' }}>
            {insight.headline.replace('{name}', firstName)}
          </h1>
          <p style={{ color:'var(--slate)', fontSize:14, lineHeight:1.65, maxWidth:360, margin:'0 auto' }}>
            {insight.body}
          </p>
        </div>

        {/* The one action */}
        <div style={{ background:'linear-gradient(135deg,rgba(0,212,170,.08),rgba(0,212,170,.02))', border:'1px solid rgba(0,212,170,.2)', borderRadius:'var(--r)', padding:'20px 20px 24px', marginBottom:14, textAlign:'center' }}>
          <Upload size={28} color="var(--teal)" style={{ marginBottom:12 }} />
          <div style={{ fontSize:15, fontWeight:600, color:'var(--white)', marginBottom:6 }}>
            Upload your bank statement to get started
          </div>
          <div style={{ fontSize:12, color:'var(--slate)', marginBottom:20, lineHeight:1.55 }}>
            Download a CSV from your bank app. Monzo, Starling, Barclays, HSBC, NatWest, Lloyds — all supported.
          </div>
          <button className="btn btn-primary btn-lg btn-full" style={{ justifyContent:'center', fontSize:15, gap:9 }}
            onClick={() => navigate('/connect')}>
            Upload my bank statement <ArrowRight size={16} />
          </button>
        </div>

        <div style={{ background:'rgba(0,212,170,.04)', border:'1px solid rgba(0,212,170,.08)', borderRadius:9, padding:'11px 14px', fontSize:12, color:'var(--slate)', lineHeight:1.6, marginBottom:20 }}>
          💡 {insight.saving}
        </div>

        <button onClick={() => navigate('/dashboard')} style={{ display:'block', width:'100%', textAlign:'center', background:'none', border:'none', color:'var(--slate-dim)', fontSize:12, cursor:'pointer', textDecoration:'underline', padding:'4px 0' }}>
          Skip for now — I'll explore first
        </button>
      </div>
    </div>
  )
}

const WRAP = { minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--navy)', padding:20, paddingTop:'max(20px, env(safe-area-inset-top))', paddingBottom:'max(20px, env(safe-area-inset-bottom))' }

